// <floating-contact> — behaviour layer for the `siclips-floating-contact` section
// (SICLIPS - WhatsApp float). The section only emits this script when it is actually
// needed: bubble mode (pre-chat popover) and/or hide-on-scroll. Pure "direct" mode with
// no hide-on-scroll ships zero JS — the FAB is just an <a> deep link.
//
// Bubble: the FAB <button aria-haspopup="dialog"> toggles a small NON-MODAL popover card
// (the page stays interactive). ESC / the X button / clicking outside close it; ESC and X
// return focus to the FAB (click-outside doesn't steal the user's new focus target).
// Hide-on-scroll: passive scroll listener, rAF-throttled (one update per frame via a
// _ticking flag), ~12px hysteresis; scrolling down hides the FIXED ROOT (CSS translates
// it off-screen), scrolling up shows it. Never hides while the bubble is open.
// Theme editor: selecting the section reveals the FAB (ignoring hide-on-scroll) and
// previews the bubble; deselecting closes it. The entrance delay itself is bypassed in
// design mode by the Liquid (--sfc-delay: 0s), no JS involved.

const HYSTERESIS = 12; // px of scroll travel before the hide/show state may flip
const SHOW_NEAR_TOP = 80; // never hidden this close to the top of the page

class FloatingContact extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-sfc-trigger]');
    this.bubble = this.querySelector('[data-sfc-bubble]');
    this._open = this._open || false;

    // Element-level listeners bind ONCE per element instance. The theme editor MOVES the
    // existing node on drag-reorder (disconnect + reconnect of the SAME element), and its
    // listeners survive the move — rebinding here would stack duplicate toggle handlers.
    if (!this._boundEl) {
      this._boundEl = true;
      if (this.dataset.mode === 'bubble' && this.trigger && this.bubble) this._bindBubbleEl();
    }

    // Document/window listeners are removed on disconnect, so (re)bind on every connect.
    if (this._docBound) return;
    this._docBound = true;
    if (this.dataset.mode === 'bubble' && this.trigger && this.bubble) this._bindBubbleDoc();
    if (this.dataset.hideOnScroll === 'true') this._bindScroll();
    if (window.Shopify && window.Shopify.designMode) this._bindEditor();
  }

  disconnectedCallback() {
    this._docBound = false;
    if (this._onDocDown) document.removeEventListener('pointerdown', this._onDocDown, true);
    if (this._onDocKey) document.removeEventListener('keydown', this._onDocKey);
    if (this._onScroll) {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onScroll);
    }
    if (this._onSelect) document.removeEventListener('shopify:section:select', this._onSelect);
    if (this._onDeselect) document.removeEventListener('shopify:section:deselect', this._onDeselect);
  }

  // ---------- bubble (pre-chat popover) ----------
  _bindBubbleEl() {
    this.trigger.addEventListener('click', () => (this._open ? this._close() : this._openBubble()));

    const x = this.querySelector('[data-sfc-close]');
    if (x) x.addEventListener('click', () => this._close());
  }

  _bindBubbleDoc() {
    // capture phase so clicks swallowed by other widgets still close the popover
    this._onDocDown = (e) => {
      if (this._open && !this.contains(e.target)) this._close(false);
    };
    document.addEventListener('pointerdown', this._onDocDown, true);

    this._onDocKey = (e) => {
      if (e.key === 'Escape' && this._open) this._close();
    };
    document.addEventListener('keydown', this._onDocKey);
  }

  _openBubble(focus = true) {
    this._open = true;
    this.classList.add('sfc--open');
    this.classList.remove('sfc--hidden'); // an open chat is never scroll-hidden
    this.trigger.setAttribute('aria-expanded', 'true');
    if (focus && this.bubble) this.bubble.focus({ preventScroll: true });
  }

  _close(refocus = true) {
    if (!this._open) return;
    this._open = false;
    this.classList.remove('sfc--open');
    this.trigger.setAttribute('aria-expanded', 'false');
    if (refocus) this.trigger.focus({ preventScroll: true });
  }

  // ---------- hide on scroll ----------
  _bindScroll() {
    this._lastY = window.scrollY || 0;
    this._ticking = false;
    this._onScroll = () => {
      if (this._ticking) return;
      this._ticking = true;
      window.requestAnimationFrame(() => this._scrollUpdate());
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onScroll, { passive: true });
  }

  _scrollUpdate() {
    this._ticking = false;
    const y = window.scrollY || 0;
    const dy = y - this._lastY;
    if (Math.abs(dy) < HYSTERESIS) return; // hysteresis: ignore jitter, keep state
    this._lastY = y;
    if (this._open) {
      this.classList.remove('sfc--hidden');
      return;
    }
    if (dy > 0 && y > SHOW_NEAR_TOP) this.classList.add('sfc--hidden');
    else this.classList.remove('sfc--hidden');
  }

  // ---------- theme editor ----------
  _bindEditor() {
    this._onSelect = (e) => {
      if (!e.target.contains(this)) return;
      this.classList.remove('sfc--hidden');
      if (this.dataset.mode === 'bubble' && this.bubble && !this._open) this._openBubble(false);
    };
    this._onDeselect = (e) => {
      if (!e.target.contains(this)) return;
      this._close(false);
    };
    document.addEventListener('shopify:section:select', this._onSelect);
    document.addEventListener('shopify:section:deselect', this._onDeselect);
  }
}

if (!customElements.get('floating-contact')) {
  customElements.define('floating-contact', FloatingContact);
}
